import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, IconButton, Checkbox, InputGroup,
  InputLeftElement, Icon, Tooltip, Textarea, Radio, RadioGroup, Stack, Select,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiEdit2, FiSearch, FiRotateCcw, FiMenu } from 'react-icons/fi';
import api from '../api';

export default function RowGroupsModal({ isOpen, onClose, onRefresh }) {
  const [groups, setGroups] = useState([]);
  const [viewFilters, setViewFilters] = useState([]);
  const [filterVisibility, setFilterVisibility] = useState({});
  const [query, setQuery] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', color: '#2d3748', sort_order: '', default_project_type: 'sales' });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [undeleteTarget, setUndeleteTarget] = useState(null);
  const [undeleteReason, setUndeleteReason] = useState('');
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const toast = useToast();

  const fetchGroups = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      if (showDeleted) params.set('include_deleted', 'true');
      const { data } = await api.get(`/row-groups/all?${params.toString()}`);
      setGroups(data);
    } catch (e) {}
  }, [query, showDeleted]);

  useEffect(() => {
    if (isOpen) { fetchGroups(); fetchViewFilters(); setShowForm(false); setEditingId(null); setDeleteTarget(null); setUndeleteTarget(null); }
  }, [isOpen, fetchGroups]);

  const fetchViewFilters = async () => {
    try { const { data } = await api.get('/view-filters'); setViewFilters(data.filter(f => !f.deleted_at)); } catch(e) {}
  };

  const initFilterVis = () => {
    const vis = {};
    viewFilters.forEach(f => { vis[f.id] = 'hide'; });
    return vis;
  };

  const resetForm = () => { setForm({ name: '', description: '', color: '#2d3748', sort_order: '' }); setFilterVisibility(initFilterVis()); };
  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.name) { toast({ title: 'Name required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/row-groups/${editingId}`, form);
      } else {
        await api.post('/row-groups', { ...form, view_filter_visibility: filterVisibility });
      }
      await fetchGroups(); setShowForm(false); setEditingId(null); resetForm();
      if (onRefresh) onRefresh();
      toast({ title: 'Saved', status: 'success', duration: 1500 });
    } catch (e) {
      toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  const openEdit = (g) => {
    setForm({ name: g.name, description: g.description || '', color: g.color || '#2d3748', sort_order: g.sort_order || '', default_project_type: g.default_project_type || 'sales' });
    setEditingId(g.id); setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/row-groups/${deleteTarget}`, { data: { reason: deleteReason } });
      toast({ title: 'Group deleted', status: 'info', duration: 1500 });
      await fetchGroups(); setDeleteTarget(null); setDeleteReason('');
      if (onRefresh) onRefresh();
    } catch (e) {
      toast({ title: e.response?.data?.error || 'Cannot delete', status: 'error', duration: 3000 });
      setDeleteTarget(null); setDeleteReason('');
    }
  };

  const handleUndelete = async () => {
    if (!undeleteTarget) return;
    try {
      await api.post(`/row-groups/${undeleteTarget}/undelete`, { reason: undeleteReason });
      toast({ title: 'Group restored', status: 'success', duration: 1500 });
      await fetchGroups(); setUndeleteTarget(null); setUndeleteReason('');
      if (onRefresh) onRefresh();
    } catch (e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  // Drag and drop
  const handleDragStart = (idx) => { setDragIdx(idx); };
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDragEnd = async () => {
    if (dragIdx === null || dragOverIdx === null || dragIdx === dragOverIdx) {
      setDragIdx(null); setDragOverIdx(null); return;
    }
    const reordered = [...groups];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dragOverIdx, 0, moved);
    // Update sort_order based on new positions
    const order = reordered.map((g, i) => ({ id: g.id, sort_order: (i + 1) * 10 }));
    setGroups(reordered.map((g, i) => ({ ...g, sort_order: (i + 1) * 10 })));
    setDragIdx(null); setDragOverIdx(null);
    try {
      await api.put('/row-groups/reorder', { order });
      if (onRefresh) onRefresh();
    } catch (e) { toast({ title: 'Reorder failed', status: 'error', duration: 2000 }); await fetchGroups(); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <Flex justify="space-between" pr={8}>
            Row Groups
            <Button size="xs" leftIcon={<FiPlus />} colorScheme="blue" variant="outline"
              onClick={() => { resetForm(); setEditingId(null); setShowForm(!showForm); }}>              {showForm ? 'Cancel' : 'Add'}
            </Button>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          <HStack mb={3} spacing={2}>
            <InputGroup size="xs">
              <InputLeftElement><Icon as={FiSearch} color="gray.400" boxSize={3} /></InputLeftElement>
              <Input pl={7} placeholder="Search groups..." value={query} onChange={e => setQuery(e.target.value)} />
            </InputGroup>
            <Checkbox size="sm" isChecked={showDeleted} onChange={e => setShowDeleted(e.target.checked)}>
              <Text fontSize="10px" whiteSpace="nowrap">{showDeleted ? 'Hide Deleted' : 'Show Deleted'}</Text>
            </Checkbox>
          </HStack>

          {showForm && (
            <Box bg="blue.50" p={3} borderRadius="md" mb={4}>
              <VStack spacing={2}>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={3}><FormLabel fontSize="xs" mb={0.5}>Name *</FormLabel>
                    <Input size="xs" value={form.name} onChange={f('name')} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Colour</FormLabel>
                    <Input size="xs" type="color" value={form.color} onChange={f('color')} h="26px" p={0} border="none" /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Order</FormLabel>
                    <Input size="xs" type="number" value={form.sort_order} onChange={f('sort_order')} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Default Type</FormLabel>
                    <Select size="xs" value={form.default_project_type} onChange={f('default_project_type')}>
                      <option value="sales">Sales</option>
                      <option value="administration">Administration</option>
                      <option value="workflow_template">Workflow Template</option>
                    </Select></FormControl>
                </HStack>
                <FormControl><FormLabel fontSize="xs" mb={0.5}>Description</FormLabel>
                  <Textarea size="xs" rows={2} value={form.description} onChange={f('description')}
                    placeholder="Hover tooltip description..." /></FormControl>
                {!editingId && viewFilters.length > 0 && (
                  <Box w="100%">
                    <Text fontSize="xs" fontWeight="600" color="gray.600" mb={1}>Visibility in View Filters</Text>
                    <Box maxH="180px" overflowY="auto" border="1px" borderColor="gray.200" borderRadius="md" p={2} bg="white">
                      {viewFilters.map(vf => (
                        <Flex key={vf.id} align="center" justify="space-between" py={1} borderBottom="1px" borderColor="gray.50">
                          <HStack spacing={2}>
                            <Text fontSize="11px" fontWeight="500">{vf.name}</Text>
                            {!!vf.is_system && <Badge fontSize="7px" colorScheme="blue">System</Badge>}
                          </HStack>
                          <RadioGroup size="sm" value={filterVisibility[vf.id] || 'hide'}
                            onChange={(val) => setFilterVisibility(p => ({ ...p, [vf.id]: val }))}>
                            <Stack direction="row" spacing={2}>
                              <Radio value="show"><Text fontSize="9px">Show</Text></Radio>
                              <Radio value="hide"><Text fontSize="9px">Hide</Text></Radio>
                              <Radio value="hide_if_empty"><Text fontSize="9px">Hide if Empty</Text></Radio>
                            </Stack>
                          </RadioGroup>
                        </Flex>
                      ))}
                    </Box>
                  </Box>
                )}
                <Button size="xs" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                  onClick={handleSave} isLoading={saving} alignSelf="flex-end">
                  {editingId ? 'Update' : 'Add Group'}
                </Button>
              </VStack>
            </Box>
          )}

          {deleteTarget && (
            <Box bg="red.50" p={3} borderRadius="md" mb={3}>
              <Text fontSize="12px" fontWeight="600" color="red.700" mb={2}>
                Delete "{groups.find(g => g.id === deleteTarget)?.name}"?
              </Text>
              <FormControl mb={2}><FormLabel fontSize="xs" mb={0.5}>Reason</FormLabel>
                <Textarea size="xs" rows={2} value={deleteReason} onChange={e => setDeleteReason(e.target.value)}
                  placeholder="Why is this group being deleted?" /></FormControl>
              <HStack justify="flex-end">
                <Button size="xs" variant="ghost" onClick={() => { setDeleteTarget(null); setDeleteReason(''); }}>Cancel</Button>
                <Button size="xs" colorScheme="red" onClick={handleDelete}>Confirm Delete</Button>
              </HStack>
            </Box>
          )}

          {undeleteTarget && (
            <Box bg="blue.50" p={3} borderRadius="md" mb={3}>
              <Text fontSize="12px" fontWeight="600" color="blue.700" mb={2}>
                Restore "{groups.find(g => g.id === undeleteTarget)?.name}"?
              </Text>
              <FormControl mb={2}><FormLabel fontSize="xs" mb={0.5}>Reason for restoring</FormLabel>
                <Textarea size="xs" rows={2} value={undeleteReason} onChange={e => setUndeleteReason(e.target.value)}
                  placeholder="Why is this group being restored?" /></FormControl>
              <HStack justify="flex-end">
                <Button size="xs" variant="ghost" onClick={() => { setUndeleteTarget(null); setUndeleteReason(''); }}>Cancel</Button>
                <Button size="xs" colorScheme="blue" onClick={handleUndelete}>Restore Group</Button>
              </HStack>
            </Box>
          )}

          {groups.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>No groups found.</Text>
          ) : (
            <VStack spacing={0} align="stretch">
              {groups.map((g, idx) => (
                <Tooltip key={g.id} label={g.description || ''} fontSize="xs" placement="top" hasArrow
                  isDisabled={!g.description} maxW="300px" whiteSpace="pre-wrap">
                  <Flex
                    draggable={!g.deleted_at}
                    onDragStart={() => handleDragStart(idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDragEnd={handleDragEnd}
                    p={2} border="1px" borderRadius="md" align="center" justify="space-between"
                    mb={1}
                    borderColor={dragOverIdx === idx ? 'blue.400' : g.deleted_at ? 'red.200' : 'gray.100'}
                    borderTopWidth={dragOverIdx === idx ? '3px' : '1px'}
                    opacity={dragIdx === idx ? 0.4 : g.deleted_at ? 0.5 : 1}
                    bg={g.deleted_at ? 'red.50' : 'white'}
                    cursor={g.deleted_at ? 'default' : 'default'}
                    transition="border 0.15s"
                  >
                    {/* Drag handle */}
                    {!g.deleted_at && (
                      <Box cursor="grab" mr={2} color="gray.300" _hover={{ color: 'gray.500' }}
                        onMouseDown={(e) => e.currentTarget.parentElement.setAttribute('draggable', 'true')}>
                        <FiMenu size={14} />
                      </Box>
                    )}
                    {g.deleted_at && <Box w="22px" />}

                    <HStack spacing={2} flex={1}>
                      <Box w="14px" h="14px" borderRadius="sm" bg={g.color || '#2d3748'} flexShrink={0} />
                      <Box flex={1}>
                        <Flex align="center" gap={2}>
                          <Text fontSize="12px" fontWeight="600">{g.name}</Text>
                          {g.is_system && <Badge fontSize="7px" colorScheme="blue">System</Badge>}
                          {g.deleted_at && <Badge fontSize="7px" colorScheme="red">DELETED</Badge>}
                          <Badge fontSize="7px" colorScheme="gray" variant="outline">{g.project_count || 0} projects</Badge>
                          {g.default_project_type && g.default_project_type !== 'sales' && (
                            <Badge fontSize="7px" colorScheme={g.default_project_type === 'administration' ? 'orange' : 'purple'}>
                              {g.default_project_type === 'administration' ? 'Admin' : 'Template'}
                            </Badge>
                          )}
                        </Flex>
                        {g.description && <Text fontSize="10px" color="gray.500" noOfLines={1}>{g.description}</Text>}
                        <HStack fontSize="9px" color="gray.400" spacing={2}>
                          <Text>Order: {g.sort_order}</Text>
                          {g.delete_reason && <Text color="red.400">Deleted: {g.delete_reason}</Text>}
                          {g.undelete_reason && <Text color="blue.400">Restored: {g.undelete_reason}</Text>}
                        </HStack>
                      </Box>
                    </HStack>
                    <HStack spacing={0}>
                      {!g.deleted_at ? (
                        <>
                          <IconButton icon={<FiEdit2 />} size="xs" variant="ghost" onClick={() => openEdit(g)} aria-label="Edit" />
                          <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                            onClick={() => { setDeleteTarget(g.id); setDeleteReason(''); }}
                            isDisabled={g.project_count > 0}
                            aria-label="Delete" title={g.project_count > 0 ? 'Has active projects — rename instead' : 'Delete group'} />
                        </>
                      ) : (
                        <IconButton icon={<FiRotateCcw />} size="xs" variant="ghost" colorScheme="blue"
                          onClick={() => { setUndeleteTarget(g.id); setUndeleteReason(''); }} aria-label="Restore" />
                      )}
                    </HStack>
                  </Flex>
                </Tooltip>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}
